/**
 * Reusable field error component for displaying validation errors below form fields.
 * Uses Zod validation messages from validateFieldValue().
 */

import { Typography, type SxProps, type Theme } from '@mui/material';
import type { ValidationContext } from '../../types';

export type FieldErrorProps = {
  /** Error message to display (from Zod validation). */
  error?: string;
  /** Whether to actually show the error (controls visibility). Defaults to true. */
  show?: boolean;
  /** Inline validation severity (e.g. bulk-edit brand/contact access uses warning). */
  severity?: 'error' | 'warning';
  /** Optional sx overrides for custom styling. */
  sx?: SxProps<Theme>;
};

/**
 * Renders a validation error message below a form field.
 * Returns null if no error is provided or if show is false.
 */
export const FieldError = ({
  error,
  show = true,
  severity = 'error',
  sx,
}: FieldErrorProps): React.ReactElement | null => {
  if (!error || !show) return null;
  return (
    <Typography
      variant="caption"
      color={severity === 'warning' ? 'warning' : 'error'}
      sx={[{ mt: 0.5, ml: 1.75 }, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}
    >
      {error}
    </Typography>
  );
};

/**
 * Determines whether a field error should be shown based on validation context.
 * Errors are shown only when the field has been touched (user interacted with it
 * or it was marked touched after a failed submit).
 *
 * @param fieldKey - The key/id of the field to check
 * @param validationContext - The validation context from the form
 * @returns true if the error should be displayed, false otherwise
 */
export function shouldShowFieldError(
  fieldKey: string,
  validationContext?: ValidationContext
): boolean {
  if (!validationContext) return false; // No context = don't show errors
  const { touchedFields } = validationContext;
  return touchedFields?.has(fieldKey) === true;
}
